var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import './polyfill';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import * as Sentry from '@sentry/browser';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { v4 as uuidv4 } from 'uuid';
import App from './App';
import CrashPageBasic from './views/CrashPageBasic';
import { fetchDynamicConfigs, fetchExperiments, fetchFeatureFlags, globalStatsig, initializeStatsig, isStatsigNetworkError, } from './statsig';
import { getLinkTokenPayload, getModalMountNode, getSemverString, } from './utils';
import storageAvailable from './utils/storage';
import './events/client-events';
import './events/segment-events';
import { resetStore, createStore } from './state';
import { convertToState, initializeLinkParams, selectModalSessionId, } from './state/slices/slice-link-params';
import { irisClient } from './tracking/iris';
import checkHasWriteJob from './utils/check-has-write-job';
import { resetRouter } from './Router/Router';
import { generateSearchSessionId } from './state/slices/slice-segment';
import { getClient, resetClient } from './clients/link-api';
import { addArrayToContextFromObjectByTruthiness, getTimeSinceOpen, setUpDatadog, shouldHaveRumSessionReplay, } from './tracking/datadog';
import { ERRORS } from './consts/errors';
import { getParentOrigin, postParentWindowMessage } from './utils/post-message';
import getSdkMiddleware from './sdk-interface';
// import { setErrorCodesToSkip } from './events/client-events/error';
datadogLogs.init({
    clientToken: 'pub5fe009e80bce226882dd3017da3ee5be',
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    sampleRate: 100,
    service: 'link',
});
export var __state = {
    modalSessionId: null,
};
export var __close = function () {
    var mountNode = getModalMountNode();
    if (mountNode) {
        ReactDOM.unmountComponentAtNode(mountNode);
        document.body.removeChild(mountNode);
    }
    void Sentry.close();
    datadogLogs.logger.info('Modal closed');
    // Reload so subsequent openings of the modal will not have the state persisted.
    // We may want something like that eventually but currently this is how our app works.
    // Flush all iris calls before reload
    /**
     * Don't pass callback fn to flush function trackjs 2.0.7 introduces breaking change where callback doesn't run when queue is empty
     * */
    void irisClient.flush().then(function () {
        resetStore();
        resetRouter();
        resetClient();
    });
};
function logInitialization(options, linkTokenPayload) {
    var initLoggingParams = {
        initializationOptions: options.initializationOptions,
        clientHostname: options.clientHostname,
        useDarkMode: options.useDarkMode,
    };
    if (options.initializationTimestamp)
        Object.assign(initLoggingParams, {
            firstContentfulPaintTime: Date.now() - options.initializationTimestamp,
        });
    datadogLogs.addLoggerGlobalContext('sdkVersion', __assign({ complete: getSemverString(options.version) }, options.version));
    datadogLogs.addLoggerGlobalContext('isReauth', !!linkTokenPayload.accountId);
    datadogLogs.addLoggerGlobalContext('initTime', options.initializationTimestamp || Date.now());
    datadogLogs.logger.info('Modal initialized', initLoggingParams);
}
export var __init = function (options) { return __awaiter(void 0, void 0, void 0, function () {
    var linkToken, uniqueUserIdFromPortal, version, deviceMetadata, sdk, platform, _modalSessionIdOverride, _demoModeEnabled, experimentsOverride, featureFlagsOverride, dynamicConfigsOverride, modalSessionId, alreadyExists, mountNode, linkTokenPayload, featureFlags, fireClientEvent, fireClientEventAndAppClipEvent, uniqueUserId, localStorageAvailable, experiments, dynamicConfigs, e_1, linkError, errorMessage, opts, store, storeModalSessionId;
    return __generator(this, function (_a) {
        switch (_a.label) {
            case 0:
                linkToken = options.linkToken, uniqueUserIdFromPortal = options.uniqueUserId, version = options.version, deviceMetadata = options.deviceMetadata, sdk = options.sdk, platform = options.platform, _modalSessionIdOverride = options._modalSessionIdOverride, _demoModeEnabled = options._demoModeEnabled, experimentsOverride = options.experiments, featureFlagsOverride = options.featureFlags, dynamicConfigsOverride = options.dynamicConfigs;
                modalSessionId = _modalSessionIdOverride || uuidv4();
                alreadyExists = getModalMountNode();
                if (alreadyExists)
                    return [2 /*return*/];
                mountNode = document.createElement('div');
                mountNode.setAttribute('id', 'pinwheel-link');
                document.body.appendChild(mountNode);
                featureFlags = {
                    guidedSwitching: false,
                };
                fireClientEvent = function (eventName, payload) {
                    datadogLogs.logger.info("Client event fired (".concat(eventName, ")"), {
                        eventName: eventName,
                        payload: payload,
                        timeSinceOpen: getTimeSinceOpen(),
                    });
                    postParentWindowMessage({
                        type: 'PINWHEEL_EVENT',
                        eventName: eventName,
                        payload: payload || {},
                    });
                };
                try {
                    linkTokenPayload = getLinkTokenPayload(linkToken);
                    datadogLogs.addLoggerGlobalContext('hasWriteJob', checkHasWriteJob(linkTokenPayload.requiredJobs));
                }
                catch (e) {
                    datadogLogs.logger.error('invalid link token provided, payload parsing failed');
                    // eslint-disable-next-line no-console
                    console.error(e);
                    ReactDOM.render(React.createElement(CrashPageBasic, { initialLinkError: ERRORS.invalidLinkToken, fireClientEvent: fireClientEvent }), mountNode);
                    return [2 /*return*/];
                }
                fireClientEventAndAppClipEvent = function (eventName, payload) {
                    var _a;
                    if (linkTokenPayload.isAppClip) {
                        // TODO: Right now getClient is only initialized after the first calls to
                        // link-api, so link events prior to that will not be sent to back end.
                        // For the purposes of showing progress on device 1, that is not a blocker, but
                        // it is something to keep in mind.
                        void ((_a = getClient()) === null || _a === void 0 ? void 0 : _a.postAppClipEvent({ name: eventName, data: payload }));
                    }
                    fireClientEvent(eventName, payload);
                };
                // Can delete at later point for now just for testing PP-6996
                if (linkTokenPayload._uid) {
                    datadogLogs.logger.info("_uid encountered with value: ".concat(linkTokenPayload._uid, " & uniqueUserIdFromPortal: ").concat(uniqueUserIdFromPortal));
                }
                uniqueUserId = linkTokenPayload._uid || uniqueUserIdFromPortal || uuidv4();
                setUpDatadog(options, modalSessionId, uniqueUserId);
                logInitialization(options, linkTokenPayload);
                localStorageAvailable = storageAvailable('localStorage');
                if (!uniqueUserIdFromPortal && localStorageAvailable) {
                    datadogLogs.logger.error('Link initialized with no unique user id', {
                        fromLocalStorage: localStorage.getItem('unique_user_id'),
                        generatedUniqueUserId: uniqueUserId,
                    });
                }
                if (!localStorageAvailable) {
                    datadogLogs.logger.error('Local storage not available for link', {
                        generatedUniqueUserId: uniqueUserId,
                    });
                }
                experiments = {};
                dynamicConfigs = {};
                _a.label = 1;
            case 1:
                _a.trys.push([1, 3, , 4]);
                return [4 /*yield*/, initializeStatsig(uniqueUserId, linkTokenPayload, modalSessionId)];
            case 2:
                _a.sent();
                // These statsig value are currently using uniqueUserId as the identifier,
                // but when we fully transition to using the Customer User Model,
                // we should be just using the _uid (which is the customer_user_id)
                featureFlags = featureFlagsOverride || fetchFeatureFlags();
                globalStatsig.featureFlags = featureFlags;
                experiments = experimentsOverride || fetchExperiments();
                globalStatsig.experiments = experiments;
                if (experiments.skipIntroScreen) {
                    if (!linkTokenPayload.hasUserAuthenticationData) {
                        linkTokenPayload.skipIntroScreen = true;
                    }
                }
                dynamicConfigs =
                    dynamicConfigsOverride || fetchDynamicConfigs(linkTokenPayload.tokenId);
                addArrayToContextFromObjectByTruthiness('featureFlags', featureFlags);
                addArrayToContextFromObjectByTruthiness('experiments', experiments);
                addArrayToContextFromObjectByTruthiness('dynamicConfigs', dynamicConfigs);
                datadogLogs.logger.info('Async loading complete');
                return [3 /*break*/, 4];
            case 3:
                e_1 = _a.sent();
                datadogLogs.logger.error('Could not resolve workspace feature flags');
                // eslint-disable-next-line no-console
                console.error(e_1);
                linkError = isStatsigNetworkError(e_1)
                    ? ERRORS.networkError
                    : null;
                errorMessage = isStatsigNetworkError(e_1)
                    ? null
                    : String(e_1.message);
                ReactDOM.render(React.createElement(CrashPageBasic, { initialLinkError: linkError, errorMessage: errorMessage, fireClientEvent: fireClientEventAndAppClipEvent }), mountNode);
                return [2 /*return*/];
            case 4:
                if (shouldHaveRumSessionReplay()) {
                    datadogRum.startSessionReplayRecording();
                }
                opts = {
                    fireClientEvent: fireClientEventAndAppClipEvent,
                    postParentWindowMessage: postParentWindowMessage,
                    linkToken: linkToken,
                    uniqueUserId: uniqueUserId,
                    version: version,
                    deviceMetadata: deviceMetadata,
                    platform: platform,
                    sdk: sdk,
                    featureFlags: featureFlags,
                    experiments: experiments,
                    dynamicConfigs: dynamicConfigs,
                    localStorageAvailable: localStorageAvailable,
                    _demoModeEnabled: _demoModeEnabled,
                    useDarkMode: options.useDarkMode,
                };
                store = createStore({
                    preloadedState: {
                        linkParams: convertToState(opts, linkTokenPayload),
                    },
                });
                /*
                 * Data requires "search_session_id" to be set, regardless of the user ever
                 * looking at a Search screen or searching at all, we do this here so
                 * this is always the case.
                 */
                store.dispatch(generateSearchSessionId());
                store.dispatch(initializeLinkParams({
                    linkTokenPayload: linkTokenPayload,
                    options: __assign(__assign({}, options), { featureFlags: featureFlags, experiments: experiments, dynamicConfigs: dynamicConfigs, _modalSessionIdOverride: modalSessionId, localStorageAvailable: localStorageAvailable, uniqueUserId: uniqueUserId }),
                }));
                storeModalSessionId = selectModalSessionId(store.getState());
                /*
                 * We store a map with all the client events in memory since we
                 * shouldn't be storing this in Redux. (All redux state should be
                 * serializable)
                 *
                 * TODO: Look for a better alternative
                 */
                if (!window.clientEmitters)
                    window.clientEmitters = {};
                window.clientEmitters[storeModalSessionId] = getSdkMiddleware(window.__LINK_VERSION__, fireClientEventAndAppClipEvent);
                ReactDOM.render(React.createElement(Provider, { store: store },
                    React.createElement(App, { opts: opts, unMount: __close, linkTokenPayload: linkTokenPayload, modalSessionId: storeModalSessionId })), mountNode);
                return [2 /*return*/];
        }
    });
}); };
function addEventListenersToWindow() {
    var _this = this;
    __state.modalSessionId = uuidv4();
    window.addEventListener('message', function (event) { return __awaiter(_this, void 0, void 0, function () {
        var dataType, eventName, parentOrigin;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!event.data)
                        return [2 /*return*/];
                    if (typeof event.data !== 'object')
                        return [2 /*return*/];
                    dataType = event.data.type;
                    eventName = 'eventName' in event.data ? event.data.eventName : null;
                    parentOrigin = getParentOrigin();
                    datadogLogs.logger.info("Link message received (".concat(dataType, ") (").concat(eventName, ")"), {
                        event: event.data,
                    });
                    if (!(dataType === 'PINWHEEL_INIT')) return [3 /*break*/, 2];
                    if (parentOrigin && event.origin !== parentOrigin) {
                        datadogLogs.logger.warn("Link received 'PINWHEEL_INIT' message from an untrusted origin (".concat(event.origin, ")"));
                        return [2 /*return*/];
                    }
                    return [4 /*yield*/, __init(event.data.payload)];
                case 1:
                    _a.sent();
                    _a.label = 2;
                case 2:
                    if (dataType === 'PINWHEEL_PROGRAMMATIC_CLOSE') {
                        if (parentOrigin && event.origin !== parentOrigin) {
                            datadogLogs.logger.warn("Link received 'PINWHEEL_PROGRAMMATIC_CLOSE' message from an untrusted origin (".concat(event.origin, ")"));
                            return [2 /*return*/];
                        }
                        __close();
                    }
                    return [2 /*return*/];
            }
        });
    }); });
}
addEventListenersToWindow();
